import * as React from 'react'

import { Box, Icon, List, Text } from '@factorialco/deprecated-design-system'
import { BookkeeperCompanyIncidenceOptionFragment } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import i18n from 'modules/core/lib/i18n'

import {
  incidenceNeedsCompanyValidation,
  isIncidenceActiveForCompany,
} from 'modules/bookkeepers/lib/incidenceOptions'

import Item from './_Item'

export interface OptionsType {
  id: string
  name: string
  disabled: boolean
  identifier: string
  getTranslatedTitle: () => string
  getTranslatedDescription: () => string
}

interface Props {
  illustration: React.ComponentProps<typeof Icon.Large>['icon']
  title: string
  description: string
  options: OptionsType[]
  companyIncidenceSettings: BookkeeperCompanyIncidenceOptionFragment[]
  onTogglerChange: (type: OptionsType, isValidation?: boolean) => void
  isSettings?: boolean
  onLoadingIncidenceOption: boolean
}

const IncidenceOptionsSection = ({
  illustration,
  title,
  description,
  options,
  companyIncidenceSettings,
  onTogglerChange,
  isSettings = false,
  onLoadingIncidenceOption,
}: Props) => {
  const buildDescription = (isTracked: boolean, isValidated: boolean | undefined) => {
    if (!isTracked) return i18n.t('bookkeepers.clients.preferences.configuration.untracked')

    if (isValidated) return i18n.t('bookkeepers.clients.preferences.configuration.needs_validation')

    return i18n.t('bookkeepers.clients.preferences.configuration.no_validation')
  }

  const getDescription = (type: OptionsType) => {
    if (isSettings) {
      return type.getTranslatedDescription()
    } else {
      const isValidated = incidenceNeedsCompanyValidation(companyIncidenceSettings, type)
      const activated = isIncidenceActiveForCompany(companyIncidenceSettings, type)

      return buildDescription(activated, isValidated)
    }
  }
  const [loadingIncidenceOption, setLoadingIncidenceOption] = React.useState<string | null>(null)

  const handleTogglerChange = (incidenceOption: OptionsType) => {
    setLoadingIncidenceOption(incidenceOption.id)
    onTogglerChange(incidenceOption)
  }

  return (
    <Box flexDirection="row" justifyContent="spaceBetween" alignItems="flexStart" paddingY="s40">
      <Box width="full" gap="s16">
        <Box justifyContent="spaceBetween" flexDirection="row">
          <Icon.Large icon={illustration} color="radicalRed1000" />
        </Box>
        <Box maxWidth="s400" gap="s12">
          <Text.Header2>{title}</Text.Header2>
          <Text.Normal weight="normal" color="grey800" size="300">
            {description}
          </Text.Normal>
          <Box
            paddingTop="s4"
            width="s32"
            border={{
              bottom: {
                width: 's2',
                style: 'solid',
                color: 'radicalRed1000',
              },
            }}
          />
        </Box>
      </Box>
      <Box width="full">
        <List>
          {options.map((type) => (
            <Item
              key={type.id + type.name}
              title={type.getTranslatedTitle()}
              description={getDescription(type)}
              onTogglerChange={() => handleTogglerChange(type)}
              disabled={
                (loadingIncidenceOption === type.id && onLoadingIncidenceOption) || type.disabled
              }
              active={isIncidenceActiveForCompany(companyIncidenceSettings, type)}
              onSetValidation={() => onTogglerChange(type, true)}
              needsValidation={incidenceNeedsCompanyValidation(companyIncidenceSettings, type)}
              isSettings={isSettings}
            />
          ))}
        </List>
      </Box>
    </Box>
  )
}

export default observer(IncidenceOptionsSection)
